import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
interface PixelMaskProps {
  variant: 'desktop' | 'mobile';
}
interface Cell {
  c: number;
  r: number;
  open: boolean;
}
const COLS = {
  desktop: 24,
  mobile: 10
};
export function PixelMask({
  variant
}: PixelMaskProps) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const cols = COLS[variant];
  const [rows, setRows] = useState(12);
  const [revealed, setRevealed] = useState(false);
  const [intro, setIntro] = useState(true);
  const [flipped, setFlipped] = useState<Set<number>>(new Set());
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const {
        width,
        height
      } = el.getBoundingClientRect();
      if (width === 0) return;
      setRows(Math.max(6, Math.round(height / (width / cols))));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [cols]);
  const cells = useMemo<Cell[]>(() => {
    const list: Cell[] = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        list.push({
          c,
          r,
          open: isOpenCell(c, r, cols, rows, variant)
        });
      }
    }
    return list;
  }, [cols, rows, variant]);
  const edgeCells = useMemo(() => {
    const openAt = (c: number, r: number) => cells[r * cols + c]?.open;
    const result: number[] = [];
    cells.forEach((cell, i) => {
      const neighbors = [[cell.c - 1, cell.r], [cell.c + 1, cell.r], [cell.c, cell.r - 1], [cell.c, cell.r + 1]].filter(([a, b]) => a >= 0 && b >= 0 && a < cols && b < rows);
      if (neighbors.some(([a, b]) => openAt(a, b) !== cell.open)) result.push(i);
    });
    return result;
  }, [cells, cols, rows]);
  useEffect(() => {
    const t1 = window.setTimeout(() => setRevealed(true), 120);
    const t2 = window.setTimeout(() => setIntro(false), 1500);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, []);
  useEffect(() => {
    if (reduce || !revealed || edgeCells.length === 0) return;
    const id = window.setInterval(() => {
      const next = new Set<number>();
      const n = 3 + Math.floor(Math.random() * 4);
      for (let k = 0; k < n; k++) next.add(edgeCells[Math.floor(Math.random() * edgeCells.length)]);
      setFlipped(next);
    }, 650);
    return () => window.clearInterval(id);
  }, [reduce, revealed, edgeCells]);
  return <div ref={ref} aria-hidden className="pointer-events-none absolute inset-0 grid" style={{
    gridTemplateColumns: `repeat(${cols}, 1fr)`,
    gridTemplateRows: `repeat(${rows}, 1fr)`
  }} data-id="element-200" data-mp="3ruh6xz9eqqtg">
      {cells.map((cell, i) => {
      const isFlipped = flipped.has(i);
      const showEmber = revealed && cell.open && isFlipped;
      const showCanvas = !revealed || !cell.open && !isFlipped;
      const delay = intro && revealed && !reduce ? (cols - cell.c) * 28 + cell.r * 14 : 0;
      return <div key={i} className={`${showEmber ? 'bg-ember' : 'bg-canvas'} transition-[opacity,background-color] duration-200 ease-out`} style={{
        opacity: showCanvas ? 1 : showEmber ? 0.85 : 0,
        transitionDelay: `${delay}ms`,
        width: 'calc(100% + 1px)',
        height: 'calc(100% + 1px)'
      }} data-id="element-201" data-mp="1zxh3qih8g68e" data-mpi={i} />;
    })}
    </div>;
}
function hash(c: number, r: number): number {
  const s = Math.sin(c * 127.1 + r * 311.7) * 43758.5453;
  return s - Math.floor(s);
}
function isOpenCell(c: number, r: number, cols: number, rows: number, variant: 'desktop' | 'mobile'): boolean {
  const x = (c + 0.5) / cols;
  const y = (r + 0.5) / rows;
  const jitter = (hash(c, r) - 0.5) * 0.14;
  if (variant === 'mobile') {
    return y > 0.46 - x * 0.12 + jitter * 0.6;
  }
  const left = y < 0.45 ? 0.62 + (0.45 - y) * 0.5 : 0.62 - (y - 0.45) * 3.6;
  if (x < left + jitter) return false;
  const right = 0.72 + (1 - y) * 1.1;
  if (x > right + jitter) return false;
  return true;
}